import React, { useMemo } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Trophy, MapPin, Clock, Loader2, Star, CalendarPlus, Info, Newspaper } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useMatchDetails, useFavouriteTeams, useMatchNews } from '../../hooks/useSports'
import { downloadFixtureIcs } from '../../lib/icsFile'
import { cn } from '../../lib/utils'

function fmtDateTime(iso, locale) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  } catch {
    return iso
  }
}

// Status of a match → human label. Maps the upstream codes to AR/EN.
function statusLabel(item, t) {
  const s = String(item?.status || '').toUpperCase()
  if (s === 'IN_PLAY' || s === 'LIVE' || s === 'PAUSED') return t('sports.match.statusLive')
  if (s === 'FINISHED' || s === 'AWARDED') return t('sports.match.statusFinished')
  if (s === 'POSTPONED') return t('sports.match.statusPostponed')
  if (s === 'CANCELLED' || s === 'SUSPENDED') return t('sports.match.statusCancelled')
  return t('sports.match.statusScheduled')
}

// Compact W/D/L pill for a given outcome from one team's perspective.
function FormPill({ outcome }) {
  const map = {
    W: { tone: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30', label: 'W' },
    D: { tone: 'bg-amber-500/10 text-amber-500 border-amber-500/30', label: 'D' },
    L: { tone: 'bg-red-500/10 text-red-500 border-red-500/30', label: 'L' },
  }
  const s = map[outcome] || { tone: 'bg-muted text-muted-foreground border-border', label: '—' }
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold border',
        s.tone,
      )}
      dir="ltr"
    >
      {s.label}
    </span>
  )
}

// Derive last-5 outcomes for a given teamId from a list of H2H or
// recent matches. `matches` is football-data's match[] shape.
function deriveForm(teamId, matches, limit = 5) {
  if (!teamId || !Array.isArray(matches)) return []
  const finishedFor = matches
    .filter((m) => m.status === 'FINISHED' && (m.homeTeam?.id === teamId || m.awayTeam?.id === teamId))
    .slice(0, limit)
  return finishedFor.map((m) => {
    const homeScore = m.score?.fullTime?.home ?? null
    const awayScore = m.score?.fullTime?.away ?? null
    if (homeScore == null || awayScore == null) return null
    const wasHome = m.homeTeam?.id === teamId
    const teamScore = wasHome ? homeScore : awayScore
    const oppScore = wasHome ? awayScore : homeScore
    if (teamScore > oppScore) return 'W'
    if (teamScore < oppScore) return 'L'
    return 'D'
  }).filter(Boolean)
}

export function SportsMatchPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const { data, loading, error } = useMatchDetails(id)
  const favTeams = useFavouriteTeams()

  const match = data?.match
  const h2h = data?.head2head || []
  const aggregates = data?.aggregates

  // News from our own news_articles, ILIKE-matched on team short
  // names. Loose match by design — better to over-include a couple
  // of borderline articles than miss the obvious ones.
  const matchNews = useMatchNews(
    {
      homeName: match?.homeTeam?.name,
      awayName: match?.awayTeam?.name,
      homeShortName: match?.homeTeam?.shortName,
      awayShortName: match?.awayTeam?.shortName,
    },
    5,
  )

  const goals = useMemo(() => {
    const list = []
    // football-data's goal events live on the match object itself
    // (free tier exposes a basic `goals` array when populated).
    if (Array.isArray(match?.goals)) {
      for (const g of match.goals) {
        list.push({
          minute: g.minute,
          scorer: g.scorer?.name,
          team: g.team?.id,
          teamName: g.team?.name,
        })
      }
    }
    list.sort((a, b) => (a.minute || 0) - (b.minute || 0))
    return list
  }, [match])

  const homeForm = useMemo(() => deriveForm(match?.homeTeam?.id, h2h), [match?.homeTeam?.id, h2h])
  const awayForm = useMemo(() => deriveForm(match?.awayTeam?.id, h2h), [match?.awayTeam?.id, h2h])

  const goBack = () => navigate(-1)

  return (
    <div className="space-y-3 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5" dir="auto">
            <Trophy className="w-4 h-4 text-app-primary-500" />
            {t('sports.match.title')}
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-3 text-xs text-red-600" dir="auto">{error}</CardContent>
        </Card>
      ) : !match ? (
        <EmptyState icon={Trophy} title={t('sports.match.notFound')} />
      ) : (
        <>
          {/* Hero — both crests, score / kickoff, status. */}
          <Card className="overflow-hidden">
            <CardContent className="p-4 sm:p-5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-app-primary-500 text-center mb-2" dir="auto">
                {match.competition?.name}
                {match.matchday ? ` · ${t('sports.match.matchday', { n: match.matchday })}` : ''}
              </p>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
                <TeamHero
                  team={match.homeTeam}
                  isFavourite={favTeams.teamIds.has(match.homeTeam?.id)}
                  onToggleFavourite={favTeams.toggle}
                  t={t}
                />
                <CentreScore match={match} locale={locale} t={t} />
                <TeamHero
                  team={match.awayTeam}
                  isFavourite={favTeams.teamIds.has(match.awayTeam?.id)}
                  onToggleFavourite={favTeams.toggle}
                  t={t}
                />
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-muted-foreground" dir="auto">
                <span className="px-2 py-0.5 rounded-full bg-muted">
                  {statusLabel(match, t)}
                </span>
                {match.utcDate && (
                  <span className="inline-flex items-center gap-1" dir="ltr">
                    <Clock className="w-3 h-3" />
                    {fmtDateTime(match.utcDate, locale)}
                  </span>
                )}
              </div>
              {match.venue && (
                <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground" dir="auto">
                  <MapPin className="w-3 h-3" />
                  {match.venue}
                </p>
              )}
              {/* Calendar button — only meaningful for upcoming matches. */}
              {(match.status === 'SCHEDULED' || match.status === 'TIMED') && (
                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    onClick={() => downloadFixtureIcs(match)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-app-primary-500/30 bg-app-primary-500/5 text-app-primary-500 hover:bg-app-primary-500/10 transition-colors"
                    dir="auto"
                  >
                    <CalendarPlus className="w-3.5 h-3.5" />
                    {t('sports.match.addToCalendar')}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Goals timeline */}
          {goals.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1" dir="auto">
                  {t('sports.match.goals')}
                </p>
                <ul className="space-y-1.5">
                  {goals.map((g, i) => {
                    const isHome = g.team === match.homeTeam?.id
                    return (
                      <li
                        key={i}
                        className={cn(
                          'flex items-center gap-2 text-xs',
                          isHome ? 'justify-start' : 'justify-end',
                        )}
                        dir="auto"
                      >
                        {isHome ? (
                          <>
                            <span className="font-mono text-muted-foreground w-9 text-center">
                              {g.minute}'
                            </span>
                            <span className="text-foreground">⚽ {g.scorer || g.teamName}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-foreground">{g.scorer || g.teamName} ⚽</span>
                            <span className="font-mono text-muted-foreground w-9 text-center">
                              {g.minute}'
                            </span>
                          </>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Form (last 5) per team — derived from H2H. */}
          {(homeForm.length > 0 || awayForm.length > 0) && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-3 px-1" dir="auto">
                  {t('sports.match.form')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <p className="text-xs text-foreground font-medium truncate" dir="auto">
                      {match.homeTeam?.shortName || match.homeTeam?.name}
                    </p>
                    <div className="flex gap-1">
                      {homeForm.length === 0
                        ? <span className="text-[11px] text-muted-foreground">—</span>
                        : homeForm.map((o, i) => <FormPill key={i} outcome={o} />)
                      }
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-foreground font-medium truncate" dir="auto">
                      {match.awayTeam?.shortName || match.awayTeam?.name}
                    </p>
                    <div className="flex gap-1">
                      {awayForm.length === 0
                        ? <span className="text-[11px] text-muted-foreground">—</span>
                        : awayForm.map((o, i) => <FormPill key={i} outcome={o} />)
                      }
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lineups note. Free tier of football-data doesn't include
              starting XI / bench — surface that politely so the user
              doesn't keep looking for the section. Skipped entirely
              once paid-tier lineup data lands (would render a real
              section here instead). */}
          <div className="px-1">
            <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1" dir="auto">
              <Info className="w-3 h-3 flex-shrink-0" />
              {t('sports.match.lineupsUnavailable')}
            </p>
          </div>

          {/* Related news from the news_articles cache */}
          {matchNews.articles.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1 inline-flex items-center gap-1.5" dir="auto">
                  <Newspaper className="w-3.5 h-3.5 text-app-primary-500" />
                  {t('sports.match.relatedNews')}
                </p>
                <div className="space-y-2">
                  {matchNews.articles.map((a) => (
                    <RouterLink key={a.id} to={`/news/${a.id}`} className="block">
                      <div className="flex items-start gap-2 p-2 -mx-1 rounded-md hover:bg-accent transition-colors">
                        {a.image_url && (
                          <img
                            src={a.image_url}
                            alt=""
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                            className="w-12 h-12 rounded-md object-cover flex-shrink-0 bg-muted"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground line-clamp-2" dir="auto">
                            {a.title}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5" dir="auto">
                            {a.source?.name}
                          </p>
                        </div>
                      </div>
                    </RouterLink>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Head-to-head list */}
          {h2h.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1" dir="auto">
                  {t('sports.match.h2h')}
                </p>
                {aggregates && (
                  <div className="grid grid-cols-3 gap-1 mb-3 text-[10px] text-center" dir="auto">
                    <AggregateCell
                      label={match.homeTeam?.shortName || match.homeTeam?.name}
                      wins={aggregates.homeTeam?.wins}
                      draws={aggregates.homeTeam?.draws}
                      losses={aggregates.homeTeam?.losses}
                    />
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        {t('sports.match.played')}
                      </span>
                      <span className="text-base font-bold text-foreground font-mono">
                        {aggregates.numberOfMatches ?? 0}
                      </span>
                    </div>
                    <AggregateCell
                      label={match.awayTeam?.shortName || match.awayTeam?.name}
                      wins={aggregates.awayTeam?.wins}
                      draws={aggregates.awayTeam?.draws}
                      losses={aggregates.awayTeam?.losses}
                    />
                  </div>
                )}
                <ul className="space-y-1.5">
                  {h2h.map((m) => {
                    const homeScore = m.score?.fullTime?.home ?? '—'
                    const awayScore = m.score?.fullTime?.away ?? '—'
                    return (
                      <li
                        key={m.id}
                        className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-xs"
                        dir="auto"
                      >
                        <span className="text-end text-foreground truncate" dir="auto">
                          {m.homeTeam?.shortName || m.homeTeam?.name}
                        </span>
                        <span className="font-mono tabular-nums px-2 py-0.5 rounded bg-muted text-foreground text-[11px]">
                          {homeScore} – {awayScore}
                        </span>
                        <span className="text-start text-foreground truncate" dir="auto">
                          {m.awayTeam?.shortName || m.awayTeam?.name}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

function TeamHero({ team, isFavourite, onToggleFavourite, t }) {
  if (!team) return <div />
  return (
    <div className="flex flex-col items-center gap-2 min-w-0">
      {/* Crest + name link to the team detail page so the user can
          drill into squad / form / recent results without going back
          to the list first. */}
      <RouterLink
        to={`/sports/teams/${team.id}`}
        className="flex flex-col items-center gap-1 group"
      >
        {team.crest && (
          <img
            src={team.crest}
            alt=""
            loading="lazy"
            className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
          />
        )}
        <p className="text-sm font-semibold text-foreground text-center leading-tight group-hover:text-app-primary-500 transition-colors" dir="auto">
          {team.shortName || team.name}
        </p>
      </RouterLink>
      <button
        type="button"
        onClick={() => onToggleFavourite({
          id: team.id,
          name: team.name,
          shortName: team.shortName,
          crestUrl: team.crest || null,
        })}
        aria-pressed={isFavourite}
        aria-label={t(isFavourite ? 'sports.unfavourite' : 'sports.favourite')}
        className={cn(
          'inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors',
          isFavourite
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-500'
            : 'border-input text-muted-foreground hover:text-amber-500',
        )}
        dir="auto"
      >
        <Star className={cn('w-3 h-3', isFavourite && 'fill-current')} />
        {t(isFavourite ? 'sports.unfavourite' : 'sports.favourite')}
      </button>
    </div>
  )
}

function CentreScore({ match, locale, t }) {
  const status = String(match.status || '').toUpperCase()
  const isLive = status === 'IN_PLAY' || status === 'LIVE' || status === 'PAUSED'
  const isFinished = status === 'FINISHED' || status === 'AWARDED'
  const hasScore = isLive || isFinished
  if (hasScore) {
    return (
      <div className="flex flex-col items-center gap-0.5 min-w-[80px]">
        <p className={cn(
          'text-3xl sm:text-4xl font-bold font-mono tabular-nums',
          isLive ? 'text-red-500' : 'text-foreground',
        )}>
          {match.score?.fullTime?.home ?? 0}
          <span className="text-muted-foreground mx-1.5">–</span>
          {match.score?.fullTime?.away ?? 0}
        </p>
        {isLive && match.minute != null && (
          <span className="text-[11px] font-semibold text-red-500 uppercase tracking-wide animate-pulse">
            {match.minute}'
          </span>
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center gap-1 min-w-[80px]">
      <p className="text-lg font-semibold text-foreground font-mono tabular-nums" dir="ltr">
        {match.utcDate
          ? new Date(match.utcDate).toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', {
              hour: 'numeric', minute: '2-digit', hour12: true,
            })
          : '—'}
      </p>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {t('sports.match.kickoff')}
      </span>
    </div>
  )
}

function AggregateCell({ label, wins = 0, draws = 0, losses = 0 }) {
  return (
    <div className="space-y-0.5">
      <p className="text-foreground font-medium truncate" dir="auto">{label}</p>
      <p className="font-mono tabular-nums text-muted-foreground" dir="ltr">
        <span className="text-emerald-500">{wins}</span>
        <span className="mx-0.5">–</span>
        <span className="text-amber-500">{draws}</span>
        <span className="mx-0.5">–</span>
        <span className="text-red-500">{losses}</span>
      </p>
    </div>
  )
}
